<script lang="ts" setup>
import { ref, watch, onMounted } from "vue";
import { useMainStore } from "../store/main";
import { usePGRESTAPIStore } from "../store/pgrest_api";
import { useI18n } from "vue-i18n";
import Guarantee from "../classes/DB_Entities/Guarantee";
import { ElMessage } from "element-plus";
import Decision from "../classes/Decision";
import EntityField from "./EntityField.vue";
import { useGenericMethodsVariables } from "../composables/genericMethodsVariables";
import PGRESTResult from "../classes/PGRESTResult";
import AcceptAllPending from "./AcceptAllPending.vue";
import UserType from "../enums/UserType";

const props = defineProps<{
  guaranteeid: number;
  newEntity?: boolean;
  view_only: boolean;
}>();

const emits = defineEmits(["save", "close", "fail-save"]);

const mainStore = useMainStore();
const pgrestapi = usePGRESTAPIStore();
const { t } = useI18n({ useScope: "global" });

const loanAgreementsList = ref(new Array<any>());

const {
  showError,
  updateDecision,
  hasPendingChanges,
  isLoading,
  fullObj,
  table_name,
  clickAcceptAll,
  loadData,
  save,
  close,
  cleanObject,
  resetObjects,
} = useGenericMethodsVariables();

onMounted(async () => {
  table_name.value = Guarantee.getTableNameStatic();
  cleanObject.value = new Guarantee();

  console.log("guaranteeid", props.guaranteeid, "newEntity", props.newEntity);

  await loadLoanAgreements();

  if (props.newEntity === true) {
    resetObjects();
    return;
  }

  isLoading.value = true;
  await loadData(props.guaranteeid);
  isLoading.value = false;
});

const loadLoanAgreements = async () => {
  let result: PGRESTResult = new PGRESTResult();
  loanAgreementsList.value = [];

  let query = `view_mosloanagreement_view1?&approve_status=eq.1&debtor_approve_status=eq.1`;
  
  if (mainStore.loggedUser.user_type === UserType.Agent) {
    query += `&allowed_users=cs.{${mainStore.loggedUser.id}}`;
  } else if (mainStore.loggedUser.user_type === UserType.Supervisor) {
    query += `&portfolio_allowed_users=cs.{${mainStore.loggedUser.id}}`;
  }

  result = await pgrestapi.get(query);

  if (result.error) {
    showError(result.error.message);
    return;
  }

  for (let i = 0; i < result.data.length; i++) {
    loanAgreementsList.value.push({
      value: result.data[i].id,
      label: result.data[i].ar_sumbashs + " (" + result.data[i].fullname + ")",
    });
  }
};

const onDecision = async (decision: Decision) => {
  isLoading.value = true;

  await updateDecision(decision);

  await loadData(props.guaranteeid);

  isLoading.value = false;
};

const onPreSave = async () => {
  //   console.log(JSON.stringify(fullObj.value));
  if (props.newEntity === true) {
    if (fullObj.value.loan_agreement_id.originalValue === 0) {
      ElMessage({
        showClose: true,
        message: t("errors.missing_loan_agreement"),
        type: "warning",
        duration: 1500,
      });
      return;
    }

    if (fullObj.value.debtor_cid.originalValue === "") {
      ElMessage({
        showClose: true,
        message: t("errors.missing_debtor_cid"),
        type: "warning",
        duration: 1500,
      });
      return;
    }
    else {
      let result = await pgrestapi.get(`debtor?select=id&cid=eq.${fullObj.value.debtor_cid.originalValue}`)
      if (result.data.length === 0) {
        ElMessage({
          showClose: true,
          message: t("errors.no_debtor_with_this_cid"),
          type: "warning",
          duration: 1500,
        });
        return;
      }
    }
  }

  delete fullObj.value.loan_agreement;

  await save();
  resetObjects();
};

const fullAgreementGuaranteeList = [
  {
    value: true as boolean,
    label: t("global.true"),
  },
  {
    value: false as boolean,
    label: t("global.false"),
  },
];
</script>

<template>
  <div v-loading="isLoading">
    <el-form>
      <AcceptAllPending v-show="hasPendingChanges && !props.view_only" @accept="clickAcceptAll(props.guaranteeid)" />
      <p />
      <el-row :gutter="10">
        <el-col :span="24">
          <EntityField type="select" :object="fullObj.loan_agreement_id"
            :label="$t('GuaranteesDetails.loan_agreement_number')" :options="loanAgreementsList"
            :is-disabled="props.view_only" @decision="onDecision" />
        </el-col>
        <el-col :span="24">
          <EntityField type="input" :label="$t('GuaranteesDetails.debtor_cid')" :object="fullObj.debtor_cid"
            :is-disabled="props.view_only" @decision="onDecision" />
        </el-col>
        <el-col :span="24">
          <EntityField type="select" :label="$t('GuaranteesDetails.full_agreement_guarantee')"
            :object="fullObj.egguhsh_sunolou_sumbas" :options="fullAgreementGuaranteeList" :is-disabled="props.view_only"
            @decision="onDecision" />
        </el-col>
        <el-col :span="24" v-if="
          fullObj.egguhsh_sunolou_sumbas !== undefined &&
          fullObj.egguhsh_sunolou_sumbas.originalValue === false
        ">
          <EntityField type="money" :label="$t('GuaranteesDetails.guaranteed_amount')" :object="fullObj.poso_egg"
            :is-disabled="props.view_only" @decision="onDecision" />
        </el-col>
        <el-col :span="24">
          <EntityField type="input" :label="$t('GuaranteesDetails.comments')" :object="fullObj.comments"
            :is-disabled="props.view_only" @decision="onDecision" />
        </el-col>
        <el-col :span="24">
          <el-form-item class="btn-wrapper-components">
            <el-button v-if="!props.view_only" type="primary" @click="onPreSave">{{ $t('generic.save') }}</el-button>
            <el-button @click="close">{{ $t('generic.close') }}</el-button>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
  </div>
</template>

<style scoped></style>
